import { Mui, MuiIcons } from '@leap4/frontend';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { AXIOS_INSTANCE } from '../../../api/axiosInstance';

const {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} = Mui;
const { ExpandMore: ExpandMoreIcon, HelpOutline: HelpOutlineIcon } = MuiIcons;

export interface IModelVersion {
  version: string;
  summary: string;
  replacedBecause: string | null;
  parameters: Array<{ label: string; value: string }>;
}

export interface IModelExplain {
  method: string;
  backcastMethod: string;
  horizonDays: number;
  trendDamping: number;
  driftPerDayPct: number;
  minProfileSamples: number;
  modelRiskFallbackPct: number;
  lookbackDays: number;
  settledThrough: string;
  fittedFrom: string;
  currentVersion: string;
  history: IModelVersion[];
  completion: Array<{ ageDays: number; sharePct: number }>;
  band: Array<{
    horizonDays: number;
    measuredPct: number | null;
    driftPct: number;
    totalPct: number | null;
  }>;
}

/** Each is independent, so a page can carry only the part its reader needs. */
export type MethodologySection =
  | 'version'
  | 'source'
  | 'whatYouSee'
  | 'projection'
  | 'forecast'
  | 'band'
  | 'howWeKnow'
  | 'limits';

const ALL_SECTIONS: MethodologySection[] = [
  'source',
  'whatYouSee',
  'projection',
  'forecast',
  'band',
  'howWeKnow',
  'limits',
  'version',
];

/**
 * Sections that can only honestly describe the deployed model. v1 had no damping at all, its band came
 * from a constant rather than a measurement, and the completion curve is served as measured today
 * rather than as it stood when an older model read it — shown beside a superseded version's figures,
 * each would assert something untrue about it.
 *
 * What survives is what does not depend on which model ran: where the data comes from, the three kinds
 * of number, and how accuracy is established.
 */
const CURRENT_ONLY_SECTIONS: MethodologySection[] = [];

interface IProps {
  idCampaign: string;
  event: string;
  /** Shown as the event the figures describe, since the model is fitted per event rather than once. */
  eventLabel?: string;
  /**
   * Which model is being read. Omitted means the deployed one — pass it on a page that can show a
   * superseded version, or the panel describes a model the reader is not looking at.
   */
  method?: string;
  /** Omit for all of them. Order is respected, so a page can lead with what matters to it. */
  sections?: MethodologySection[];
  defaultExpanded?: boolean;
  title?: string;
}

const Term = ({ children }: { children: any }): JSX.Element => (
  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{children}</Box>
);

const Para = ({ children }: { children: any }): JSX.Element => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{children}</Typography>
);

const Heading = ({ children }: { children: any }): JSX.Element => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{children}</Typography>
);

/**
 * What the predicted numbers mean, drawn from the parameters the model is actually running on rather
 * than from prose written alongside it — the two drift apart on the first change to a constant.
 *
 * Written for the desk, not for us: no deployment or scheduling detail, and the statistics named
 * plainly where naming them is the clearest thing to do.
 */
const PredictionMethodology = (props: IProps): JSX.Element | null => {
  const {
    idCampaign,
    event,
    eventLabel,
    method,
    sections = ALL_SECTIONS,
    defaultExpanded = false,
    title,
  } = props;
  const [model, setModel] = useState<IModelExplain | null>(null);

  useEffect(() => {
    if (!idCampaign || !event) return;
    AXIOS_INSTANCE.get('/prediction/explain', {
      params: { id_campaign: idCampaign, event, ...(method ? { method } : {}) },
    })
      .then((res) => setModel(res.data ?? null))
      .catch(() => setModel(null));
  }, [idCampaign, event, method]);

  if (!model) return null;

  // The version being read, which is not the deployed one on a superseded tab.
  const readMethod = method ?? model.method;
  const version = readMethod.match(/v\d+/)?.[0] ?? model.currentVersion;
  // Same maths either way, but a replay covers every past day while a live run accrues one a night.
  const runKind = readMethod.endsWith('-backcast') ? 'replay' : 'live';
  const label = `${version} · ${runKind}`;
  const superseded = version !== model.currentVersion;
  const entry = model.history.find((h) => h.version === version) ?? null;

  // One row of arithmetic from the real curve, which lands harder than another sentence describing it.
  // Picked mid-curve: at the extremes the division is either trivial or absurd.
  const example =
    model.completion.find((c) => c.sharePct > 50 && c.sharePct < 95) ??
    model.completion.find((c) => c.sharePct > 0) ??
    null;
  const scored = model.band.some((b) => b.measuredPct !== null);
  const bandRow = model.band.find((b) => b.totalPct !== null && b.horizonDays > 0) ?? null;
  const bandTotal = bandRow?.totalPct ?? 0;
  const bandExample = bandRow
    ? `A forecast of 200 for a date ${bandRow.horizonDays} day${bandRow.horizonDays === 1 ? '' : 's'} ahead, shown as ±${bandTotal.toFixed(1)}%, means we expect roughly ${Math.round(200 * (1 - bandTotal / 100)).toLocaleString()} to ${Math.round(200 * (1 + bandTotal / 100)).toLocaleString()}.`
    : '';
  const OBSERVED = 120;
  const worked = example
    ? `So a date ${example.ageDays === 0 ? 'from today' : `${example.ageDays} day${example.ageDays === 1 ? '' : 's'} old`} showing ${OBSERVED} conversions, at ${example.sharePct.toFixed(1)}% typically arrived by that age, projects to about ${Math.round(OBSERVED / (example.sharePct / 100)).toLocaleString()}.`
    : '';

  const body: Record<MethodologySection, JSX.Element> = {
    version: (
      <Box>
        <Heading>{superseded ? `${label} — superseded` : `${label} — in use`}</Heading>
        {entry && <Para>{entry.summary}</Para>}
        {entry?.replacedBecause && (
          <Para><Term>Replaced because:</Term> {entry.replacedBecause}</Para>
        )}
        {superseded && (
          <Para>
            Kept for like-for-like comparison against {model.currentVersion} on the same dates.
            Everything on this tab describes {label} as it ran, not the model in use.
          </Para>
        )}
        {entry && entry.parameters.length > 0 && (
          <Table size="small" sx={{ maxWidth: 720, mx: 'auto' }}>
            <TableBody>
              {entry.parameters.map((param) => (
                <TableRow key={param.label}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>{param.label}</Term></TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{param.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    ),

    source: (
      <Box>
        <Heading>What these numbers mean</Heading>
        <Para>
          <Term>Each date is the day impressions were served, not the day conversions happened.</Term>{' '}
          A conversion is credited back to the impression it is attributed to, and it can arrive up to{' '}
          {model.lookbackDays} days later.
        </Para>
        <Para>
          So a recent date is never finished. It keeps rising for {model.lookbackDays} days after the
          fact. What this feature does is estimate where an unfinished date will end up, and forecast
          dates whose impressions have not been served yet.
        </Para>
        <Para>
          <Term>A cohort</Term> is one combination of app bundle, demand tag, US state, deal partner
          and deal — the same breakdowns you group a report by. Completion curves, levels and trends
          are all fitted per cohort, and separately per event, so {eventLabel ?? event} has its own
          window, its own curve and its own error record. Nothing is shared between events.
        </Para>
      </Box>
    ),

    whatYouSee: (
      <Box>
        <Heading>Three kinds of number</Heading>
        <Para>
          Conversions arrive over the days following an impression, so a recent impression date always
          reads below where it will finish. Which kind of figure you are reading depends on its age.
          {!superseded &&
            ' Since this is the active version, projected and forecast figures are recomputed on every read, so they move through the day as conversions arrive.'}
        </Para>
        <Table size="small" sx={{ maxWidth: 720, mx: 'auto' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Settled</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Older than {model.lookbackDays} days. The final total, with no uncertainty — on or
                before {dayjs(model.settledThrough).format('MMM D')} as of today.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Projected</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Still accruing. Observed conversions scaled by the share still expected to arrive.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Forecast</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Today and ahead. No observations yet — estimated from the cohort's own history
                {!sections.includes('source') &&
                  ' (a cohort being one combination of app bundle, demand tag, state, deal partner and deal)'}
                .
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    ),

    projection: (
      <Box>
        <Heading>Projected dates</Heading>
        <Para>
          <Term>This part is the same in every version.</Term> The completion curve is measured from
          what actually arrived, not chosen by the model, and only the forecasting maths changed between
          versions. The one exception is at the youngest ages, where the estimate leans on the cohort's
          historical level — and that level does come from the forecasting maths.
        </Para>
        <Para>
          Each cohort has a completion curve — the share of an impression date's final total typically
          observed by a given age. Measured from this campaign's own history
          {superseded ? ' as it stands today, rather than as it stood when this version ran' : ''}:
        </Para>
        <Table size="small" sx={{ mb: 1, maxWidth: 420, mx: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell>Age of the impression date</TableCell>
              <TableCell align="right">Typically arrived</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {model.completion.map((c) => (
              <TableRow key={c.ageDays}>
                <TableCell>
                  {c.ageDays === 0 ? 'Same day' : `${c.ageDays} day${c.ageDays === 1 ? '' : 's'}`}
                  {c.ageDays === model.lookbackDays && ' (settled)'}
                </TableCell>
                <TableCell align="right">{c.sharePct.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Para>
          A date still accruing is its observed conversions divided by that share. {worked}
        </Para>
        <Para>
          Very recent dates have little observed data, so dividing by a small share on its own would be
          unstable. Their estimates lean more heavily on the cohort's typical day instead. As
          conversions arrive, the observation carries more of the weight, until the estimate converges
          on the final total.
        </Para>

      </Box>
    ),

    forecast: (
      <Box>
        <Heading>Forecast dates</Heading>
        <Para>
          With nothing observed yet, the forecast is built from the cohort's own settled history, in
          three parts:
        </Para>
        <Table size="small" sx={{ maxWidth: 720, mx: 'auto', mb: 1 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Level</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Its recent typical day. A median rather than an average, so one unusual day cannot drag
                it, and days that were barely observed count for less.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Trend</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Whether it has recently been rising or falling. Estimated with Theil–Sen, a method built
                to resist outliers.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Term>Day of week</Term></TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                Adjusts for the difference between, say, a Monday and a Saturday.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {superseded ? (
          <Para>
            How far that trend was carried, and how far ahead it ran, differed by version — see the
            parameters above.
          </Para>
        ) : (
          <Para>
            Beyond the range the trend was fitted on, it is damped at {model.trendDamping} per day — its
            influence roughly halves each week further out, so a cohort that grew last month is not
            projected to grow indefinitely. The forecast horizon is {model.horizonDays} days.
          </Para>
        )}
      </Box>
    ),

    band: (
      <Box>
        <Heading>
          {superseded ? `What ${label} turned out to be worth` : 'What the ± means'}
        </Heading>
        <Para>
          {superseded
            ? `Its published interval was modelled rather than measured, so it is not reproduced here. What is shown is the error ${label} actually realised, scored against the dates it forecast once they settled — the like-for-like comparison against the model in use.`
            : 'The ± is how uncertain the forecast is at that distance ahead. It comes from how wrong forecasts made the same distance ahead actually turned out to be, plus a small allowance for each further day.'}
        </Para>
        {!superseded && bandExample && <Para>{bandExample}</Para>}
        {!superseded && (
          <Para>
            It is set to cover roughly two thirds of past outcomes, so about one date in three has landed
            outside its band. That is expected rather than a failure — a band wide enough to catch
            everything would be too wide to be worth reading.
          </Para>
        )}
        {!scored && (
          <Para>
            Nothing here has been scored yet. A {runKind} run can only be measured once the dates it
            forecast have finished accruing, and{' '}
            {runKind === 'live'
              ? 'a live run records one day a night, so it takes a fortnight before its first dates settle'
              : 'no horizon yet has the minimum settled dates behind it'}
            . The replay of the same model, where one exists, is measurable today.
          </Para>
        )}
        <Table
          size="small"
          sx={superseded ? { mb: 1, maxWidth: 420, mx: 'auto' } : { mb: 1 }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Days ahead</TableCell>
              <TableCell align="right">Realised</TableCell>
              {!superseded && <TableCell align="right">Allowance</TableCell>}
              {!superseded && <TableCell align="right">Shown as</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {model.band.map((b) => (
              <TableRow key={b.horizonDays}>
                <TableCell>{b.horizonDays}</TableCell>
                <TableCell align="right">
                  {b.measuredPct == null ? '—' : `${b.measuredPct.toFixed(1)}%`}
                </TableCell>
                {!superseded && <TableCell align="right">+{b.driftPct.toFixed(1)}%</TableCell>}
                {!superseded && (
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {b.totalPct == null ? 'not yet measured' : `±${b.totalPct.toFixed(1)}%`}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!superseded && (
          <Para>
            <Term>Each row is one forecasting distance, not a running total</Term> — realised plus
            allowance gives the figure shown.
          </Para>
        )}
        {!superseded && (
          <Para>
            An allowance of {model.driftPerDayPct}% per day ahead is added on top, since measurement can
            only capture variance that has already occurred. It accumulates with the horizon, so the
            current day carries none. A distance with fewer than {model.minProfileSamples} settled dates
            behind it has not been measured yet and falls back to a flat {model.modelRiskFallbackPct}%.
          </Para>
        )}
        <Para>
          As a rule of thumb, and the banding the error figures are coloured by: under 15% is reliable
          enough to act on, 15–25% is directional, above 25% should not be leaned on alone.
        </Para>
        {!superseded && (
          <Para>
            <Term>Re-measured nightly</Term> against newly settled dates, so the interval tracks the
            model's realised accuracy as it changes. It calibrates the confidence interval, not the
            point estimate.
          </Para>
        )}
      </Box>
    ),

    howWeKnow: (
      <Box>
        <Heading>How accuracy is established</Heading>
        <Para>
          Every forecast is recorded with the date it was made and compared against the settled outcome.
          The error figures are realised accuracy, not a self-assessment.
        </Para>
        <Para>
          A model can also be back-tested across past dates using only the information available at the
          time, so a change is validated against real outcomes before it is adopted. Scoring currently begins{' '}
          <Term>{dayjs(model.fittedFrom).format('MMM D')}</Term> — it tracks how far the data reaches,
          so it moves as older dates age out. Forecasts before it had too little history behind them
          to be diagnostic.
        </Para>
      </Box>
    ),

    limits: (
      <Box>
        <Heading>Known limits</Heading>
        <Para>
          <Term>Breakdowns will not sum to the campaign total.</Term> Each grouping is estimated
          independently from its own history, and the statistics involved are non-linear, so the parts
          do not add back up to the whole. Settled dates reconcile exactly — the divergence is confined
          to projected and forecast figures.
        </Para>
        <Para>
          <Term>Longer horizons are less certain than they appear.</Term> Fewer settled dates are
          available to score them. Treat the first week as firm and the tail as indicative.
        </Para>
        {!superseded && (
          <Para>
            <Term>The point estimate is not bias-corrected.</Term> The interval learns from realised
            error; the central figure does not yet.
          </Para>
        )}
      </Box>
    ),
  };

  return (
    <Accordion defaultExpanded={defaultExpanded} elevation={1} sx={{ borderRadius: 3, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpOutlineIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title ?? 'How this is calculated'}
          </Typography>
          <Chip size="small" label={label} sx={{ height: 20, fontSize: '0.7rem' }} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {sections
          .filter((key) => !superseded || !CURRENT_ONLY_SECTIONS.includes(key))
          .map((key, index) => (
            <Box key={key}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              {body[key]}
            </Box>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default PredictionMethodology;
